import React, { useState } from 'react';
import { IAuditoriaRoundTrippingResultado, IAuditoriaRoundTrippingItem } from '../interfaces/IAuditoriaRoundTripping';

interface Props {
  resultado: IAuditoriaRoundTrippingResultado;
}

const formatCurrency = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPct = (v: number) =>
  `${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const formatScore = (v: number) =>
  `${(v * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const ItemRow: React.FC<{ item: IAuditoriaRoundTrippingItem }> = ({ item }) => {
  const [expandido, setExpandido] = useState(false);
  const ehAviso = item.status === 'AVISO';

  return (
    <>
      <tr
        onClick={() => setExpandido(!expandido)}
        style={{ cursor: 'pointer', backgroundColor: ehAviso ? '#eff6ff' : '#fff1f2' }}
      >
        <td>{item.empresaNome}</td>
        <td><strong>{item.razaoSocialContraparte}</strong></td>
        <td>{new Date(item.dataSaida).toLocaleDateString('pt-BR')}</td>
        <td>{new Date(item.dataEntrada).toLocaleDateString('pt-BR')}</td>
        <td className='text-end'>{formatCurrency(item.valorSaida)}</td>
        <td className='text-end'>{formatCurrency(item.valorEntrada)}</td>
        <td className='text-end'>
          <span style={{ color: item.similaridadeScore >= 0.95 ? '#dc2626' : '#b45309' }}>
            {formatScore(item.similaridadeScore)}
          </span>
        </td>
        <td className='text-end'>{item.diasEntreLancamentos} dias</td>
        <td className='text-end' style={{ color: ehAviso ? undefined : '#dc2626' }}>
          <strong>{formatCurrency(item.passivoTotal)}</strong>
        </td>
        <td>
          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
        </td>
      </tr>

      {expandido && (
        <tr>
          <td colSpan={10} style={{ backgroundColor: '#f8fafc', padding: '1rem' }}>

            {/* Banner de diagnóstico */}
            <div
              className='alert mb-3 py-2'
              style={ehAviso
                ? { backgroundColor: '#eff6ff', border: '1px solid #93c5fd' }
                : { backgroundColor: '#fee2e2', border: '1px solid #fca5a5' }}
            >
              <small>{item.mensagemDiagnostico}</small>
            </div>

            {/* Históricos */}
            <div className='row g-3 mb-3'>
              <div className='col-md-6'>
                <h6 className='text-muted mb-2'>Lançamento de Saída (Fornecedor)</h6>
                <table className='table table-sm table-bordered mb-0'>
                  <tbody>
                    <tr><td>Histórico</td><td>{item.historicoSaida}</td></tr>
                    <tr><td>Data</td><td>{new Date(item.dataSaida).toLocaleDateString('pt-BR')}</td></tr>
                    <tr><td>Valor</td><td className='text-end'>{formatCurrency(item.valorSaida)}</td></tr>
                  </tbody>
                </table>
              </div>
              <div className='col-md-6'>
                <h6 className='text-muted mb-2'>Lançamento de Entrada (Cliente)</h6>
                <table className='table table-sm table-bordered mb-0'>
                  <tbody>
                    <tr><td>Histórico</td><td>{item.historicoEntrada}</td></tr>
                    <tr><td>Data</td><td>{new Date(item.dataEntrada).toLocaleDateString('pt-BR')}</td></tr>
                    <tr><td>Valor</td><td className='text-end'>{formatCurrency(item.valorEntrada)}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Critérios de detecção */}
            <div className='mb-3'>
              <h6 className='text-muted mb-2'>Critérios de Detecção</h6>
              <table className='table table-sm table-bordered mb-0' style={{ maxWidth: 420 }}>
                <tbody>
                  <tr>
                    <td>Similaridade Jaro-Winkler</td>
                    <td className='text-end'>
                      <span style={{ color: '#dc2626', fontWeight: 600 }}>
                        {formatScore(item.similaridadeScore)} ≥ 90%
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td>Intervalo entre lançamentos</td>
                    <td className='text-end'>
                      <span style={{ color: '#dc2626', fontWeight: 600 }}>
                        {item.diasEntreLancamentos} dia(s) ≤ 30 dias
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td>Desvio de Valor</td>
                    <td className='text-end'>
                      <span style={{ color: '#dc2626', fontWeight: 600 }}>
                        {formatPct(item.desvioRelativo)} ≤ 5%
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Memória do passivo — só para ERRO confirmado; AVISO não calcula passivo automático */}
            {ehAviso ? (
              <div className='mb-2 text-muted' style={{ fontSize: '0.85rem' }}>
                {item.motivoAviso === 'DESPESA_RECORRENTE'
                  ? 'Despesa recorrente identificada — o histórico indica mensalidade, assinatura, licença ou ' +
                    'parcela de fornecedor (fora do escopo de Round-Tripping). Sem cálculo automático de passivo ' +
                    'fiscal; requer validação manual apenas se houver dúvida real.'
                  : 'Candidato fraco — o histórico de um dos lados explica a coincidência (retenção, ' +
                    'recebimento a maior ou devolução da mesma relação comercial). Sem cálculo automático ' +
                    'de passivo fiscal; requer validação manual antes de qualquer conclusão.'}
              </div>
            ) : (
              <div className='mb-2'>
                <h6 className='text-muted mb-2'>Passivo Fiscal Estimado</h6>
                <table className='table table-sm table-bordered mb-0' style={{ maxWidth: 420 }}>
                  <tbody>
                    <tr>
                      <td>Base de Cálculo (Valor Simulado)</td>
                      <td className='text-end'>{formatCurrency(item.baseCalculo)}</td>
                    </tr>
                    <tr>
                      <td>Tributo Principal (24% IRPJ 15% + CSLL 9%)</td>
                      <td className='text-end text-danger'><strong>{formatCurrency(item.tributoPrincipal)}</strong></td>
                    </tr>
                    <tr>
                      <td>Multa Qualificada (75% sobre Tributo)</td>
                      <td className='text-end text-danger'>{formatCurrency(item.multaQualificada)}</td>
                    </tr>
                    <tr>
                      <td>Juros SELIC Acumulados (BCB Série 11)</td>
                      <td className='text-end text-danger'>{formatCurrency(item.jurosSelic)}</td>
                    </tr>
                    <tr style={{ backgroundColor: '#fee2e2' }}>
                      <td><strong>Passivo Total Estimado</strong></td>
                      <td className='text-end text-danger'><strong>{formatCurrency(item.passivoTotal)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaRoundTrippingDetalhe: React.FC<Props> = ({ resultado }) => {
  const temErro = resultado.totalErros > 0;
  const temAviso = resultado.totalAvisos > 0;

  return (
    <div className='auditoria-card'>
      <h4>Detecção de Round-Tripping (Simulação de Receita)</h4>
      <p className='text-muted'>
        Identifica fluxos circulares entre Fornecedores (2.01.01.03.01) e Clientes/Duplicatas a
        Receber (1.01.02.02.01) com similaridade Jaro-Winkler ≥ 90%, janela de 30 dias e desvio de
        valor ≤ 5%. Só entram como saída/entrada lançamentos com evidência real de caixa (Débito de
        baixa de fornecedor / Crédito de recebimento de cliente) — provisão de despesa e
        reconhecimento de receita não são fluxo de caixa e nunca são pareados. Candidatos cujo
        histórico já explica a coincidência (retenção, recebimento a maior, devolução) ou indica
        despesa recorrente/parcelada de fornecedor (mensalidade, assinatura, licença) viram AVISO —
        candidato a revisão manual, sem cálculo automático de passivo.
      </p>

      {temErro && (
        <div className='alert alert-danger mb-4'>
          <strong>ERRO DE AUDITORIA: Circularidade Financeira Detectada</strong>
          <br />
          <small>{resultado.mensagemDirecionamento}</small>
        </div>
      )}
      {!temErro && temAviso && (
        <div className='alert alert-info mb-4'>
          <strong>AVISO: Candidato(s) Fraco(s) a Circularidade Financeira</strong>
          <br />
          <small>{resultado.mensagemDirecionamento}</small>
        </div>
      )}

      {/* KPIs */}
      <div className='auditoria-kpis mb-4' style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className={`auditoria-kpi ${resultado.passivoTotalGeral > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Passivo Total Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.passivoTotalGeral)}</div>
          <div className='auditoria-kpi-help'>Tributo + Multa + Juros SELIC</div>
        </div>
        <div className={`auditoria-kpi ${resultado.tributoPrincipalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Tributo Principal (24%)</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.tributoPrincipalTotal)}</div>
          <div className='auditoria-kpi-help'>IRPJ 15% + CSLL 9%</div>
        </div>
        <div className={`auditoria-kpi ${(resultado.multaQualificadaTotal + resultado.jurosSelicTotal) > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Multa (75%) + Juros SELIC</div>
          <div className='auditoria-kpi-value'>
            {formatCurrency(resultado.multaQualificadaTotal + resultado.jurosSelicTotal)}
          </div>
          <div className='auditoria-kpi-help'>Lei 9.430/96 Art. 44 + BCB Série 11</div>
        </div>
        <div className='auditoria-kpi auditoria-kpi-disabled'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value' style={{ fontSize: '0.7rem', lineHeight: 1.4 }}>
            CC Art.167 · Lei 8.137/90 Art.1º · CPC 00
          </div>
        </div>
      </div>

      {/* Tabela de pares */}
      {resultado.itens.length === 0 ? (
        <div className='auditoria-empty-state'>
          <p>Nenhum padrão de circularidade financeira (confirmado ou candidato) identificado para o período selecionado.</p>
        </div>
      ) : (
        <div className='table-responsive'>
          <table className='table table-hover align-middle'>
            <thead className='table-light'>
              <tr>
                <th>Empresa</th>
                <th>Contraparte (Normalizada)</th>
                <th>Data Saída</th>
                <th>Data Entrada</th>
                <th className='text-end'>Valor Saída</th>
                <th className='text-end'>Valor Entrada</th>
                <th className='text-end'>Similaridade</th>
                <th className='text-end'>Intervalo</th>
                <th className='text-end'>Passivo Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {resultado.itens.map((item, idx) => (
                <ItemRow key={`${item.empresaId}-${idx}`} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Fundamentação legal */}
      {resultado.fundamentacaoLegal && (
        <details className='mt-4'>
          <summary className='text-muted' style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
            Fundamentação Legal
          </summary>
          <div className='mt-2 p-3' style={{ backgroundColor: '#f8fafc', fontSize: '0.8rem', lineHeight: 1.6 }}>
            {resultado.fundamentacaoLegal}
          </div>
        </details>
      )}
    </div>
  );
};

export default AuditoriaRoundTrippingDetalhe;
